<template>
  <div class="timeline-wrapper tw-desktop d-flex flex-column">
    <!-- Fixed Header -->
    <header class="flex-shrink-0">
      <div class="row">
        <!-- LEFT -->
        <div class="col-md-8 bg-light d-flex flex-column h-100 pr-0">
          <!-- Tabs -->
          <div class="d-flex flex-shrink-0">
            <button
              v-for="tab in visibleTabs"
              :key="tab.value"
              class="btn btn-lg rounded-0 flex-fill fs-5"
              :class="
                filter.status === tab.value
                  ? tab.activeClass
                  : tab.inActiveClass
              "
              @click="$emit('change-status', tab.value)"
            >
              {{ $t(tab.label) }}

              <div
                class="badge text-white"
                :class="
                  filter.status === tab.value
                    ? tab.badgeActive
                    : tab.badgeInActive
                "
              >
                {{ counts[tab.value] }}
              </div>
            </button>
          </div>
        </div>
        <!-- RIGHT -->
        <div class="col-md-4 bg-light d-flex flex-column h-100 pl-0">
          <!-- Header -->
          <div
            class="bg-dark text-white d-flex align-items-center justify-content-between px-3 py-2"
          >
            <strong>
              {{ todayDate }}
            </strong>

            <strong>
              {{ todayTime }}
            </strong>

            <a href="#" @click="$emit('refresh')" class="text-white">
              <i class="fas fa-fw fa-sync-alt"></i>
            </a>

            <button
              class="btn btn-secondary btn-sm"
              @click="$emit('switch-tab')"
            >
              {{ $t("account_page_timeline.ci_co") }}
            </button>
          </div>
        </div>
      </div>
      <!-- Header Content -->
    </header>

    <!-- Booking list -->
    <main class="flex-grow-1 overflow-auto">
      <template v-if="bookings.length > 0">
        <div
          v-for="(item, index) in bookings"
          :key="index"
          class="reservation-item mb-1 px-2 py-3"
          @click="handleSelectBooking(item)"
        >
          <div class="row mx-0">
            <strong class="col-2 px-18">
              {{ item.guest_name }}
            </strong>
            <span class="col-2">
              <i class="fas fa-fw fa-clock"></i>
              <span class="px-18">{{ actualInOut(item) }}</span>
            </span>
            <div class="col-2">
              <span
                class="badge justify-self-center"
                :class="
                  item.checkin_status_code === '101'
                    ? 'bg-success'
                    : 'bg-warning'
                "
              >
                {{ $t(`booking_statuses.${item.checkin_status_code}`) }}
              </span>
            </div>
            <div class="col-2">
              <span>
                <i class="fas fa-fw fa-bed"></i>
                {{ item.room_number || "--" }}

                ({{ item.number_of_rooms || "--" }})
              </span>

              <span>
                <i class="fas fa-fw fa-user"></i>

                {{ item.number_of_guests || "--" }}
              </span>
            </div>

            <div class="col-2">
              <i class="fas fa-fw fa-money-bill mr-2"></i>
              <span class="px-2 py-1" :class="paymentStatusClass(item)"
                >{{ item.total_charge || "--" }}
              </span>
            </div>
            <span class="col-2">
              <i class="fas fa-fw fa-file-alt"></i>

              {{ item.reservation_no || "--" }}
            </span>
          </div>
        </div>
      </template>

      <div v-else class="text-center p-5">
        {{ $t("account_page_timeline.no_booking") }}
      </div>
    </main>

    <b-sidebar
      id="sidebar-right"
      v-model="showSidebar"
      right
      shadow
      backdrop
      no-close-on-backdrop
      no-header
    >
      <div>
        <!-- Content -->
        <template v-if="selectedBooking">
          <div class="p-3">
            <div class="d-flex flex-column">
              <!-- Header -->
              <div
                class="bg-info text-white py-2 px-2 d-flex justify-content-between align-items-center"
              >
                <strong>{{ selectedBooking.full_name }}</strong>
                <b-button
                  variant="none"
                  class="text-white"
                  @click="showSidebar = false"
                >
                  <i class="fas fa-times"></i>
                </b-button>
              </div>

              <!-- Body -->
              <div class="p-2 small d-flex flex-column justify-content-between">
                <div>
                  <div class="row border-bottom py-2">
                    <div class="col-5 fw-bold">
                      {{ $t("account_page_timeline.room_no") }}
                    </div>
                    <div class="col-7">
                      {{ selectedBooking.room_number ?? "--" }}
                    </div>
                  </div>

                  <div class="row border-bottom py-2">
                    <div class="col-5 fw-bold">
                      {{ $t("account_page_timeline.no_of_rooms") }}
                    </div>
                    <div class="col-7">
                      {{ selectedBooking.number_of_rooms ?? "--" }}
                    </div>
                  </div>

                  <div class="row border-bottom py-2">
                    <div class="col-5 fw-bold">
                      {{ $t("account_page_timeline.rsv_no") }}
                    </div>
                    <div class="col-7">
                      {{ selectedBooking.pms_reservation_no ?? "--" }}
                    </div>
                  </div>

                  <div class="row border-bottom py-2">
                    <div class="col-5 fw-bold">
                      {{ $t("account_page_timeline.no_of_guests") }}
                    </div>
                    <div class="col-7">
                      {{ selectedBooking.number_of_guests ?? "--" }}
                    </div>
                  </div>

                  <div class="row border-bottom py-2">
                    <div class="col-5 fw-bold">
                      {{ $t("account_page_timeline.ci_co") }}
                    </div>
                    <div class="col-7">
                      {{ getBookingDateRange(selectedBooking) }}
                    </div>
                  </div>

                  <div class="row border-bottom py-2">
                    <div class="col-5 fw-bold">
                      {{ $t("account_page_timeline.total") }}
                    </div>
                    <div class="col-7">
                      <span
                        class="px-2 py-1 fw-bold"
                        :class="paymentStatusClass(selectedBooking)"
                      >
                        {{ selectedBooking.total_charge ?? "--" }}
                      </span>
                    </div>
                  </div>

                  <div class="row border-bottom py-2">
                    <div class="col-5 fw-bold">
                      {{ $t("account_page_timeline.payment") }}
                    </div>
                    <div class="col-7">
                      {{
                        $t(
                          `payment_statuses.${selectedBooking.payment_status_code}`
                        )
                      }}
                    </div>
                  </div>

                  <div class="row border-bottom py-2">
                    <div class="col-5 fw-bold">
                      {{ $t("account_page_timeline.key_code") }}
                    </div>
                    <div class="col-7">
                      <span
                        v-if="
                          !checkoutCodes.includes(
                            selectedBooking.booking_status_code
                          )
                        "
                      >
                        {{ selectedBooking.key_code ?? "--" }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="row border-bottom py-2">
                  <div class="col-5 fw-bold">
                    {{ $t("account_page_timeline.pre_check_in") }}<br />
                    {{ $t("account_page_timeline.checkin") }}<br />
                    {{ $t("account_page_timeline.checkout") }}
                  </div>

                  <div class="col-7">
                    {{ formatDateTime(selectedBooking.pre_checkin_at) }}<br />
                    {{ formatDateTime(selectedBooking.checked_in_at) }}<br />
                    {{ formatDateTime(selectedBooking.checked_out_at) }}
                  </div>
                </div>

                <!-- Passport -->
                <div class="row align-items-center py-2">
                  <div class="col-5 fw-bold">
                    {{ $t("account_page_timeline.id_passport") }}
                  </div>

                  <div class="col-7">
                    <button
                      v-if="selectedBooking.gov_id"
                      class="btn btn-secondary btn-sm w-100"
                      @click="
                        $set(
                          selectedBooking,
                          'showGovId',
                          !selectedBooking.showGovId
                        )
                      "
                    >
                      {{
                        selectedBooking.showGovId
                          ? $t("account_page_timeline.hide")
                          : $t("account_page_timeline.show")
                      }}
                    </button>

                    <button
                      v-else
                      class="btn btn-secondary btn-sm w-100"
                      disabled
                    >
                      {{ $t("account_page_timeline.no_id") }}
                    </button>
                  </div>

                  <div
                    v-if="selectedBooking.gov_id && selectedBooking.showGovId"
                    class="col-12 mt-2 text-center border"
                  >
                    <img
                      :src="selectedBooking.gov_id"
                      class="img-fluid border rounded my-2"
                      style="max-height: 300px"
                      alt="Government ID"
                    />
                  </div>
                </div>
                <!-- End of passport -->
              </div>
            </div>
          </div>
        </template>
        <div v-else class="p-3">
          <p>{{ $t("account_page_timeline.select_guest") }}</p>
        </div>
        <!-- End of content -->
      </div>
    </b-sidebar>
  </div>
</template>
<script>
import moment from "moment";
export default {
  name: "TimelineDesktop",

  props: {
    bookings: {
      type: Array,
      default: () => [],
    },

    counts: {
      type: Object,
      default: () => ({}),
    },

    filter: {
      type: Object,
      default: () => ({}),
    },

    tabStatus: {
      type: String,
      default: "checkin",
    },

    visibleTabs: {
      type: Array,
      default: () => [],
    },

    todayDate: {
      type: String,
      default: "",
    },

    todayTime: {
      type: String,
      default: "",
    },

    selectedBooking: {
      type: Object,
      default: null,
    },

    actualInOut: {
      type: Function,
      required: true,
    },

    checkoutCodes: {
      type: Array,
      default: () => [],
    },

    checkinCodes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showSidebar: false,
    };
  },

  methods: {
    formatDateTime(datetime, mode = "datetime") {
      if (!datetime) return "--";

      const formatString = mode === "date" ? "YYYY/MM/DD" : "YYYY/MM/DD HH:mm";
      return moment(datetime).format(formatString);
    },
    getBookingDateRange(booking) {
      if (!booking) return "-- - --";

      // Determine which dates to use based on pms_reservation_no
      const checkin = booking.pms_reservation_no
        ? booking.checkin_date
        : booking.checked_in_at;
      const checkout = booking.pms_reservation_no
        ? booking.checkout_date
        : booking.checked_out_at;

      return `${this.formatDateTime(checkin, "date")} - ${this.formatDateTime(
        checkout,
        "date"
      )}`;
    },
    paymentStatusClass(item) {
      return this.checkinCodes.includes(String(item.payment_status_code))
        ? "bg-success"
        : "bg-warning text-dark";
    },

    handleSelectBooking(item) {
      this.$emit("select-booking", item);
      this.showSidebar = true;
    },
  },
};
</script>

<style>
.timeline-wrapper {
  height: 100vh;
  overflow-x: hidden;
  /* Fixes dynamic address bar scaling issues on real mobile devices */
  height: 100dvh;
  /* CRITICAL: Prevents pull-to-refresh and rubber-band bounce on the main app shell */
  overscroll-behavior-y: contain;
  /* Extra safeguard: prevents accidental horizontal swipe-to-navigate gestures */
  overscroll-behavior-x: none;
}
/* Ensure the inner list allows normal, native scrolling without triggering parent bounce */
.flex-grow-1.overflow-auto {
  overscroll-behavior-y: auto;
  -webkit-overflow-scrolling: touch; /* Liquid smooth momentum scrolling for iPadOS/iOS */
}

.timeline-wrapper .btn:focus {
  box-shadow: unset;
}

.reservation-item {
  border: 1px solid #e5e7ec;
  cursor: pointer;
  transition: background 0.2s;
}
.reservation-item .px-18 {
  font-size: 18px;
}

.reservation-item:hover {
  background: #f8f9fa;
}
.reservation-item .badge {
  color: white;
  padding: 8px;
  font-weight: 500;
  text-transform: uppercase;
}
.tw-desktop .btn-lg {
  font-size: 25px;
}
.tw-desktop .bg-dark {
  font-size: 20px;
}
.tw-desktop .btn-lg,
.tw-desktop .bg-dark {
  min-height: 60px;
}
.bg-due-ci-inactive {
  background-color: #f6c6ad;
}
.bg-dark-blue {
  background-color: #194c83;
}
.bg-orange {
  background-color: #e97132;
  color: #e97132;
}
.b-sidebar {
  width: 400px !important;
}
</style>